"use client";

import { useSearchParams } from "react-router-dom";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
function CornerTicks() {
  return <>
      <span className="pointer-events-none absolute -top-px -left-px h-2.5 w-2.5 border-t border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -top-px -right-px h-2.5 w-2.5 border-t border-r border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -left-px h-2.5 w-2.5 border-b border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-2.5 w-2.5 border-b border-r border-[var(--primary)]" aria-hidden="true" />
    </>;
}
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <div className="h-1.5 w-1.5 rounded-md bg-[var(--primary)]" />
    </div>;
}
const CompanyInfo = () => <div className="mt-10 pt-8 border-t border-border">
    <p className="text-sm text-muted-foreground">
      Ledo Travel is a trading name of Swiftwind Limited.
    </p>
    <p className="text-sm text-muted-foreground mt-2">
      Company Number: 15926859
    </p>
    <p className="text-sm text-muted-foreground">
      Registered in England & Wales
    </p>
    <p className="text-sm text-muted-foreground">
      Suite 2, 360 Edge Lane, Regus Fairfield Liverpool Innovation Park,
      Liverpool, L7 9NJ, United Kingdom
    </p>
  </div>;
const privacyPolicy = {
  title: "Privacy Policy",
  lastUpdated: "Last Updated: June 2026",
  content: <div className="space-y-8 text-muted-foreground overflow-y-auto max-h-[60vh] pr-4 custom-scrollbar">
      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">1. Introduction</h3>
        <p className="text-sm leading-relaxed">
          This Privacy Policy explains how Swiftwind Limited trading as Ledo Travel
          (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) collects, uses, stores and protects your
          personal information when you visit our website, contact us or use our services.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Ledo Travel is a trading name of Swiftwind Limited.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">2. Information We Collect</h3>

        <p className="text-sm font-bold text-foreground mt-4">Personal Information</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-2">
          <li>Full name</li>
          <li>Date of birth</li>
          <li>Residential address</li>
          <li>Email address</li>
          <li>Telephone number</li>
          <li>Nationality</li>
        </ul>

        <p className="text-sm font-bold text-foreground mt-4">Driver Verification Information</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-2">
          <li>Driving licence details</li>
          <li>Driving licence images</li>
          <li>DVLA check codes</li>
          <li>International driving permit details</li>
          <li>Passport or identification documents</li>
          <li>Proof of address</li>
        </ul>

        <p className="text-sm font-bold text-foreground mt-4">Rental Information</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-2">
          <li>Vehicle booking details</li>
          <li>Vehicle collection and return records</li>
          <li>Vehicle usage information</li>
          <li>Incident and accident reports</li>
        </ul>

        <p className="text-sm font-bold text-foreground mt-4">Payment Information</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-2">
          <li>Billing details</li>
          <li>Payment records</li>
          <li>Refund records</li>
        </ul>

        <p className="text-sm leading-relaxed mt-3">
          We do not store complete payment card information.
        </p>

        <p className="text-sm font-bold text-foreground mt-4">Website Information</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-2">
          <li>IP address</li>
          <li>Browser information</li>
          <li>Device information</li>
          <li>Website usage data</li>
          <li>Cookie information</li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">3. How We Use Your Information</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>Process vehicle rental bookings</li>
          <li>Verify driver eligibility</li>
          <li>Meet insurance requirements</li>
          <li>Prevent fraud</li>
          <li>Manage customer accounts</li>
          <li>Process payments and refunds</li>
          <li>Provide customer support</li>
          <li>Comply with legal obligations</li>
          <li>Improve our services</li>
          <li>Respond to enquiries</li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">4. Legal Basis for Processing</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>Performance of a contract</li>
          <li>Compliance with legal obligations</li>
          <li>Legitimate business interests</li>
          <li>Consent where required</li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">5. Sharing Information</h3>
        <p className="text-sm leading-relaxed">
          We may share personal information with insurers, DVLA, police, local authorities,
          traffic enforcement authorities, payment processors, IT providers, vehicle telematics
          providers and professional advisers where necessary.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          We do not sell personal information to third parties.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">6. Vehicle Telematics and Tracking</h3>
        <p className="text-sm leading-relaxed">
          Some vehicles may be fitted with telematics devices or tracking systems. These systems
          may record vehicle location, speed, driving behaviour and vehicle condition alerts for
          vehicle security, insurance compliance, accident investigation and fleet management.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">7. Data Retention</h3>
        <p className="text-sm leading-relaxed">
          We retain information only for as long as necessary. Rental records may be retained
          for up to 7 years to comply with legal, insurance and accounting obligations.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">8. International Transfers</h3>
        <p className="text-sm leading-relaxed">
          Where necessary, information may be processed outside the United Kingdom.
          Appropriate safeguards will be implemented to protect personal information.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">9. Your Rights</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>Access your information</li>
          <li>Correct inaccurate information</li>
          <li>Request deletion of information</li>
          <li>Restrict processing</li>
          <li>Object to processing</li>
          <li>Request data portability</li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">10. Security</h3>
        <p className="text-sm leading-relaxed">
          We take reasonable technical and organisational measures to protect personal information
          against unauthorised access, loss, misuse or disclosure.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">11. Contact Us</h3>
        <p className="text-sm leading-relaxed">
          For privacy-related enquiries, please contact:
        </p>
        <div className="mt-3 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm font-bold text-foreground">Ledo Travel</p>
          <p className="text-sm">A trading name of Swiftwind Limited</p>
          <p className="text-sm">Email: Fleetmanagement@swiftwindltd.co.uk</p>
        </div>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">12. Changes to this Policy</h3>
        <p className="text-sm leading-relaxed">
          We may update this Privacy Policy from time to time. The latest version will always
          be available on our website.
        </p>
      </section>

      <CompanyInfo />
    </div>
};
const termsOfService = {
  title: "Terms of Service",
  lastUpdated: "Last Updated: June 2026",
  content: <div className="space-y-6 text-muted-foreground overflow-y-auto max-h-[60vh] pr-4 custom-scrollbar">
      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">1. Introduction</h3>
        <p className="text-sm leading-relaxed">
          These Terms of Service govern the use of services provided by Swiftwind Limited
          trading as Ledo Travel. By accessing this website, submitting an enquiry,
          making a reservation, or using any of our services, you agree to be bound by
          these Terms of Service.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">2. Services</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>Short-term vehicle hire</li>
          <li>Long-term vehicle hire</li>
          <li>Corporate fleet rental</li>
          <li>Van hire</li>
          <li>Chauffeur and travel-related services where available</li>
        </ul>
        <p className="text-sm leading-relaxed mt-3">
          All bookings are subject to availability, verification, insurance approval and
          acceptance by Swiftwind Limited.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">3. Driver Requirements</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>Hold a valid driving licence appropriate for the vehicle category</li>
          <li>Meet our insurance eligibility requirements</li>
          <li>Provide satisfactory identification and proof of address when requested</li>
          <li>Provide accurate and complete information during the booking process</li>
        </ul>
        <p className="text-sm leading-relaxed mt-3">
          We reserve the right to refuse or cancel any booking where eligibility requirements
          are not met.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">4. Booking and Payment</h3>
        <p className="text-sm leading-relaxed">
          Reservations are not confirmed until accepted by us. Full payment, deposit
          requirements and any additional charges must be satisfied before vehicle collection
          unless otherwise agreed in writing. Prices displayed on our website may change
          without prior notice.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">5. Security Deposits</h3>
        <p className="text-sm leading-relaxed">
          A refundable security deposit may be required. The deposit may be used to cover
          vehicle damage, missing equipment, additional cleaning costs, excess mileage charges,
          fuel shortages, traffic penalties, administrative charges and outstanding rental fees.
          Unused balances will be returned following completion of vehicle inspections and
          any necessary investigations.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">6. Vehicle Usage</h3>
        <p className="text-sm leading-relaxed">
          Vehicles supplied by Swiftwind Limited must not be used for any unlawful purpose,
          racing, competitions, speed testing, carrying passengers or goods for hire or reward
          without prior written approval, by any unauthorised driver, or outside the authorised
          geographical area without written permission.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          The hirer remains responsible for the vehicle throughout the rental period.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">7. Traffic Offences and Penalties</h3>
        <p className="text-sm leading-relaxed">
          The hirer is responsible for all charges arising during the rental period, including
          Parking Charge Notices, parking penalties, congestion charges, toll charges,
          bus lane penalties, speeding offences, vehicle clamping fees and court costs relating
          to traffic violations.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Where permitted by law, liability may be transferred directly to the hirer. Where
          liability cannot be transferred, Swiftwind Limited reserves the right to recover all
          costs incurred, together with any applicable administration fees.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">8. Accidents and Damage</h3>
        <p className="text-sm leading-relaxed">
          Any accident, theft, loss or damage involving the vehicle must be reported to us
          immediately. The hirer agrees to cooperate with insurers, provide accurate information,
          complete any required accident reports and supply details of any third parties involved.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Failure to comply may result in additional liability and may affect insurance cover.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">9. Insurance</h3>
        <p className="text-sm leading-relaxed">
          Insurance may be provided subject to eligibility and policy conditions. Insurance cover
          remains subject to applicable excess amounts, policy exclusions, driver declarations
          and compliance with rental conditions.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Certain items may not be covered by standard excess protection, including tyres,
          wheels, glass, roof and underbody damage. Full insurance details will be provided
          separately within the rental agreement.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">10. Vehicle Tracking and Telematics</h3>
        <p className="text-sm leading-relaxed">
          Some vehicles may be equipped with GPS tracking systems and telematics devices.
          These systems may collect information relating to vehicle location, vehicle speed,
          driving behaviour, vehicle condition and maintenance alerts.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Such information may be used for security, insurance compliance, fleet management
          and accident investigation purposes.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">11. Privacy and Data Protection</h3>
        <p className="text-sm leading-relaxed">
          Personal information will be processed in accordance with our Privacy Policy. By using
          our services, you consent to the collection, storage and processing of your information
          as described therein.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">12. Limitation of Liability</h3>
        <p className="text-sm leading-relaxed">
          Nothing within these Terms excludes any liability which cannot legally be excluded
          under the laws of England and Wales. To the fullest extent permitted by law,
          Swiftwind Limited shall not be liable for indirect, consequential or economic losses
          arising from the use of our services.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">13. Termination</h3>
        <p className="text-sm leading-relaxed">
          We reserve the right to suspend, refuse or terminate any booking or service where
          false information has been provided, payment obligations are not met, rental conditions
          have been breached, vehicle misuse is suspected, or insurance requirements are no
          longer satisfied.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">14. Governing Law</h3>
        <p className="text-sm leading-relaxed">
          These Terms of Service shall be governed by and construed in accordance with the
          laws of England and Wales. Any dispute arising from these Terms shall be subject to
          the exclusive jurisdiction of the courts of England and Wales.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">15. Contact Information</h3>
        <div className="mt-3 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm font-bold text-foreground">Ledo Travel</p>
          <p className="text-sm">A trading name of Swiftwind Limited</p>
          <p className="text-sm">Email: Fleetmanagement@swiftwindltd.co.uk</p>
        </div>
      </section>

      <CompanyInfo />
    </div>
};
const cookiePolicy = {
  title: "Cookie Policy",
  lastUpdated: "Last Updated: June 2026",
  content: <div className="space-y-6 text-muted-foreground overflow-y-auto max-h-[60vh] pr-4 custom-scrollbar">
      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">1. Introduction</h3>
        <p className="text-sm leading-relaxed">
          This Cookie Policy explains how Swiftwind Limited trading as Ledo Travel
          uses cookies and similar technologies when you visit our website.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Ledo Travel is a trading name of Swiftwind Limited.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">2. What Are Cookies?</h3>
        <p className="text-sm leading-relaxed">
          Cookies are small text files placed on your computer, tablet or mobile device
          when you visit a website. They help websites function properly, improve user
          experience and provide information about how visitors use the website.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Cookies do not normally contain information that directly identifies an individual user.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">3. Types of Cookies We Use</h3>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          <li>
            <strong>Essential Cookies:</strong> These cookies are necessary for website
            security, login authentication, session management and basic website functionality.
          </li>
          <li>
            <strong>Performance and Analytics Cookies:</strong> These cookies help us understand
            how visitors interact with our website, including pages visited, time spent on the
            website, website performance and visitor behaviour.
          </li>
          <li>
            <strong>Functional Cookies:</strong> These cookies remember choices you make,
            such as language preferences, form information and user settings.
          </li>
          <li>
            <strong>Marketing and Advertising Cookies:</strong> From time to time, these cookies
            may be used to measure advertising effectiveness, display relevant advertisements
            and improve marketing campaigns.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">4. Third-Party Services</h3>
        <p className="text-sm leading-relaxed">
          Our website may use third-party services which may place cookies on your device,
          including Google Analytics, Google Maps, WordPress plugins, social media integrations
          and online booking systems.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          These third parties have their own privacy and cookie policies. We encourage you to
          review their policies where applicable.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">5. Managing Cookies</h3>
        <p className="text-sm leading-relaxed">
          Most web browsers allow you to view cookies, delete cookies, block cookies or receive
          notifications before cookies are stored. You can manage cookie settings through your
          browser preferences.
        </p>
        <p className="text-sm leading-relaxed mt-3">
          Please note that disabling certain cookies may affect website functionality.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">6. Changes to This Cookie Policy</h3>
        <p className="text-sm leading-relaxed">
          We may update this Cookie Policy from time to time. Any changes will be posted on this
          page together with the updated revision date.
        </p>
      </section>

      <section>
        <h3 className="text-xl font-bold text-foreground mb-3">7. Contact Us</h3>
        <div className="mt-3 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm font-bold text-foreground">Ledo Travel</p>
          <p className="text-sm">A trading name of Swiftwind Limited</p>
          <p className="text-sm">Email: Fleetmanagement@swiftwindltd.co.uk</p>
        </div>
      </section>

      <CompanyInfo />
    </div>
};
export default function PoliciesHub() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "privacy";
  const handleTabChange = (value: string) => {
    setSearchParams({
      tab: value
    });
  };
  const currentPolicy = activeTab === "terms" ? termsOfService : activeTab === "cookies" ? cookiePolicy : privacyPolicy;
  return <section data-section-id="1888" id="policies-hub" className="relative bg-background text-foreground py-28 md:py-36 overflow-hidden">
      <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[12rem] md:text-[22rem] leading-none tracking-tighter select-none" style={{
        WebkitTextStroke: "1px var(--border)",
        color: "transparent"
      }}>
          LEGAL
        </span>

        <div className="relative z-10">
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.12em] text-muted-foreground mb-6">
            Legal Information
          </p>
          <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
            Policies &{" "}
            <span className="italic" style={{
            WebkitTextStroke: "1px var(--primary)",
            color: "transparent"
          }}>
              Conditions
            </span>
            .
          </h2>
          <VioletBeam className="mt-8 mx-auto max-w-xs" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1000px] px-5 md:px-8 mt-20 md:mt-28">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-muted/50 p-1 rounded-lg border border-border mb-12 h-14">
            <TabsTrigger value="privacy" className="
                flex h-full w-full items-center justify-center
                rounded-md
                text-sm
                font-medium
                transition-all
                data-[state=active]:bg-[var(--primary)]
                data-[state=active]:text-white
              ">
              Privacy Policy
            </TabsTrigger>

            <TabsTrigger value="terms" className="
                flex h-full w-full items-center justify-center
                rounded-md
                text-sm
                font-medium
                transition-all
                data-[state=active]:bg-[var(--primary)]
                data-[state=active]:text-white
              ">
              Terms of Service
            </TabsTrigger>

            <TabsTrigger value="cookies" className="
                flex h-full w-full items-center justify-center
                rounded-md
                text-sm
                font-medium
                transition-all
                data-[state=active]:bg-[var(--primary)]
                data-[state=active]:text-white
              ">
              Cookie Policy
            </TabsTrigger>
          </TabsList>

          <div className="relative group w-full">
            <div className="relative w-full border border-border bg-card shadow-md rounded-lg p-8 md:p-12 transition-colors duration-300 group-hover:border-[var(--primary)]">
              <CornerTicks />

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10 border-b border-border pb-8">
                <div>
                  <h3 className="font-default font-medium text-3xl tracking-tight text-foreground">
                    {currentPolicy.title}
                  </h3>
                  <p className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase mt-2">
                    {currentPolicy.lastUpdated}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-md bg-[var(--primary)]" />
                  <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--primary)] uppercase">
                    OFFICIAL DOCUMENT
                  </span>
                </div>
              </div>

              <div className="prose prose-invert max-w-none">
                {currentPolicy.content}
              </div>
            </div>
          </div>
        </Tabs>
      </div>
    </section>;
}